import { AlertService } from 'sce-core';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { NEW_SEARCH_EVENT, NEW_ROW_EVENT, DELETE_ROW_EVENT, INSERT_ROW_EVENT, DUPLICATE_ROW_EVENT } from './search-constants';
import { ResourceService } from 'sce-core';
import { UserPreferenceService } from '../../layout/home/services/user-preference.service';
import { PagedData } from './models/paged-data.model';
import { PageDefinition } from './models/page-definition.model';
import { SearchParams } from './models/search-params.model';
import { SearchStatus } from './models/search-status.model';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { SearchEvent } from './models/search-event';
import { TableRow } from './models/table-row.model';
import { TablePreference } from './models/table-preference.model';
import { Preference } from './models/preference.model';
import { Condition, SearchCriteria } from './models/search-filter-model';
import { SavedSearchConfig } from './models/saved-search-config';
import { LocalPagedDataManager } from './local-paged-data-manager.service';
import { LoaderEnabled } from '../components/loader/loader.service';
import { DatePickerConfigService } from '../components/datepicker/datepicker-config.service';

@Injectable()
export class SearchService {
  // This object represents a searchable data table in the client side. It contains the corresponding attributes and methods

  // The metadata corresponding to the data table which this object is representing
  protected metadata: any;

  // The object which contains the data of the current page
  public pagedData: PagedData<TableRow>;

  // The search criteria to be applied to fetch records from the backend
  public searchCriteria: any;

  // The deleted search criteria need to be passed as empty to the backend
  public deletedSearchCriteria: any[] = [];

  public filterCondition: Condition;

  protected dataManager: LocalPagedDataManager;

  // The subject which will send out search related events
  private searchEventSubject = new Subject<SearchEvent>();

  private savedSearchConfig: SavedSearchConfig = new SavedSearchConfig();

  // Flag indicating that a page overflow event is in progress
  private newPageEventInProgress: boolean = false;

  /** Possible Values for displayMode input ::
    *  [C] - Create
    *  [E] - Edit
    *  [V] - View (Default)
    */
  public displayMode: string = 'V';

  // Non Mandatory Conditions Count
  private nonMandatoryConditionsCount: number = 0;

  public tablePreference: TablePreference;

  // The subject which will send out listeners to the metadata fetch
  private metaDataFetchSubject: ReplaySubject<any>;

  // Variable to check whether call is coming to performSearch from pagination or not
  private isFromPageChange: Boolean = false;

  // variable to store full data in case of a locally paged table
  public fullData: any[] = [];
  //  this property is used in screens with multiple table like invbalances
  public tableIdforTablePref: string ;
  // This property is used to get tableId from searchService, where we don't have dataService
  public tableIdforSearch: string;
  public nonEdiablePrimaryKey: any ;

  public isSearchFilterInitialized: boolean = false;

  // The Table Column Attribute corresponding to the data table which this object is representing
  protected tableColumnAttribute: any;

  constructor(protected http$: HttpClient,
    public searchParams: SearchParams,
    public searchStatus: SearchStatus,
    protected resourceService: ResourceService,
    protected userPreferenceService: UserPreferenceService,
    protected alertService: AlertService,
    protected dateConfigService: DatePickerConfigService) {
    this.pagedData = new PagedData<TableRow>();
  }

  public getSavedSearchConfig(): SavedSearchConfig {
    return this.savedSearchConfig;
  }

  public clearSavedSearchConfig() {
    this.savedSearchConfig = new SavedSearchConfig();
  }

  public fetchMetadataOnly(): Observable<any> {
    return this.http$.get('api/data/' + this.searchParams.tableId + '/metadata')
      .map((response: any) => {
        if (response) {
          this.metadata = response;
        }
        return response;
      });
  }

  /**
   * This method will fetch the metadata for the search entity. Internally it will also fetch the
   * table preferences set by the user, if any
   */
  public fetchMetadata(): Observable<any> {
    if (!this.metaDataFetchSubject) {
      this.metaDataFetchSubject = new ReplaySubject<any>(1);
      // if a metadata fetch is not in progress, create a new observable. Else return the already active one
      let tableId = this.searchParams.tableId;
      if (localStorage.getItem('HELPTABLEIDS')) {
        const helpTableIds: any[] = JSON.parse(localStorage.getItem('HELPTABLEIDS'));
        helpTableIds.push(tableId);
        localStorage.setItem('HELPTABLEIDS', JSON.stringify(helpTableIds));
      } else {
        localStorage.setItem('HELPTABLEIDS', JSON.stringify([tableId]));
      }
      this.tableIdforSearch = tableId;
      const metaDataFetch = this.http$.get('api/data/' + tableId + '/metadata')
        .map((response: any) => {
          if (response) {
            this.metadata = response;
           // sessionStorage.setItem('metaData', this.metadata);
          }
          return response;
        });
        // for screens like inv balances that have multiple tables in a single screen
        // we set tableprefernces for a single table.So if we provide the value for this variable then alwayas for any table
        //  in that screen the table preferneces coresponding  to this.tableIdforTablePref will be fetched else
        //  the tablepreferences coresponding to tableId will be fetched
        this.tableIdforTablePref = this.tableIdforTablePref ? this.tableIdforTablePref : tableId;
        tableId = this.tableIdforTablePref;
      // Lets fetch the table preferences asynchronously, so that the preferences are available along with the meta data
      const tablePreferenceFetch = this.http$.get(`api/core/user/tablepreferences/` + tableId + '/')
        .map((response: any) => {
          return this.transformTablePreference(tableId, response);
        }).catch((error: any) => {
          // Lets not stop navigation. Continue as if there is no preference set
          return Observable.of(new TablePreference());
        });

      // process both the responses together, save the table preference and send out the metadata
      Observable.forkJoin(metaDataFetch, tablePreferenceFetch).subscribe((response: any) => {
        this.tablePreference = response[1];
        // initialize the page definition based on the user preferene
        const pageDefinition: PageDefinition = this.pagedData.pageDefinition;
        const userPreference = this.tablePreference.columnConfig;
        pageDefinition.sortColumn = userPreference.configuration.pageDefinitionObject['sortColumn'];
        pageDefinition.sortOrder = userPreference.configuration.pageDefinitionObject['sortOrder'];
        pageDefinition.pageSize = userPreference.configuration.pageDefinitionObject['pageSize'];

        // send the metadata received event
        this.metaDataFetchSubject.next(this.metadata);
        this.metaDataFetchSubject.complete();
      });
    }
    return this.metaDataFetchSubject.asObservable();
  }

  public getMetadata(): any {
    return this.metadata;
  }

  public setMetadata(metadata: any) {
    this.metadata = metadata;
  }

  public getPagedData(): PagedData<any> {
    return this.pagedData;
  }

  public setPagedRows(rows: any) {
     this.pagedData.pageRows =  rows;
  }

  public getSearchCriteria(): any {
    return this.searchCriteria;
  }

  public setDataManager(dataManager) {
    this.dataManager = dataManager;
  }

  public setSearchCriteria(searchCriteria: any) {
    this.searchCriteria = searchCriteria;
  }

  public getDeletedSearchCriteria(): any {
    console.log("this.deletedSearchCriteria",this.deletedSearchCriteria);
    return this.deletedSearchCriteria;
  }

  public setDeletedSearchCriteria(searchCriteria: any) {
    this.deletedSearchCriteria.push(searchCriteria);    
  }

  public getFilterCondition(): Condition {
    return this.filterCondition;
  }

  public setFilterCondition(filterCondition: Condition) {
    this.filterCondition = filterCondition;
  }

  public getIsFromPageChange() {
    return this.isFromPageChange;
  }

  public setIsFromPageChange(isFromPageChange: Boolean) {
    this.isFromPageChange = isFromPageChange;
  }

  public getTablePreference() {
    if (!this.tablePreference) {
      this.tablePreference = new TablePreference();
    }
    return this.tablePreference;
  }

  public getNonMandatoryConditionsCount(): any {
    return this.nonMandatoryConditionsCount;
  }

  public setNonMandatoryConditionsCount(nonMandatoryConditionsCount: any) {
    this.nonMandatoryConditionsCount = nonMandatoryConditionsCount;
  }

  // Returns currently focused row and columnName
  public getFocusedRow(): { row: TableRow, cName: string } {
    return this.pagedData.getFocusedRow();
  }

  // Returns currently selected rows by checkbox value
  public getSelectedRows(): Array<any> {
    return this.pagedData.getSelectedRows();
  }

  public getNonSelectedRows(): Array<any> {
    return this.pagedData.getNonSelectedRows();
  }
  public getAllRows(): Array<any> {
    return this.pagedData.getAllRows();
  }

  // Returns edited data<only edited cells per row>
  public getEditedCellsPerRow(mandatoryProps?): any[] {
    return this.pagedData.getEditedCellsPerRow(mandatoryProps);
  }

  public getEditedRows(): TableRow[] {
    return this.pagedData.getEditedRows();
  }

  // Clear markup flag<>
  /* LFWM-960 Removing the previous skip first clearing code */
  public clearMarkerFlags(): void {
    this.pagedData.clearMarkerFlags();
  }

  /**
   * This method clears the data from the search service
   */
  public clearSearchStatus() {
    this.searchStatus.hasResults = false;
    this.searchStatus.recordCount = 0;
    this.pagedData.pageRows.splice(0);
  }

  /**
   * This method invokes the search method asynchronously and sends a NEW_SEARCH event once complete.
   * This method can be used by methods which needs to trigger a search call, but do not need to handle
   * the response explicitly
   */
  public performSearch(message?: string) {
    this.fetchSearchResults()
      .subscribe((data: any) => {
        this.searchStatus.hasResults = true;
        this.searchStatus.recordCount = this.pagedData.pageDefinition.recordCount;
        const event = new SearchEvent(NEW_SEARCH_EVENT);
        event.isSearchSuccess = true;
        if (message) {
          event.message = message;
        } 
        this.publishEvent(event);
      }, (error) => {
        this.alertService.clearAll().error(error);
      });
  }

  /**
   * This method returns an observable to the search request.
   * This method can be used by methods which needs to explicitly handle the response.
   * This method however does not send a NEW_SEARCH event to the listeners
   */
  @LoaderEnabled()
  public fetchSearchResults(): Observable<Response> {
    this.pagedData.pageRows = [];
    let tableId;
    if(this.searchParams.fetchArchiveRecords && this.searchParams.archiveTableId){
     tableId = this.searchParams.archiveTableId
    } else {
      tableId = this.searchParams.tableId;
    }
    const pageInfoReq = {
      tableId: tableId,
      pageNo: this.pagedData.pageDefinition.pageNumber,
      sortColumn: this.pagedData.pageDefinition.sortColumn,
      sortOrder: this.pagedData.pageDefinition.sortOrder,
      pageSize: this.pagedData.pageDefinition.pageSize,
      fullFetch: this.searchParams.isFullFetch,
      fetchAllrecords: this.searchParams.paginateLocally,
      fetchArchiveRecords: this.searchParams.fetchArchiveRecords,
      };
    const payload = {
      pageInfo: pageInfoReq,
      searchCriteria:
        this.formatSearchCriteria(this.searchCriteria, this.searchParams.additionalParamMap, this.filterCondition, pageInfoReq.tableId)
    };
    const dataUrl$: any
      = this.http$.post(`api/data/` + tableId
        + `/loadByPage`, JSON.stringify(payload));
    return dataUrl$
      .map((response: any) => {
        return this.transformSearchResponse(response);
      })
      .catch((errorObject: any) => {
        if (errorObject.error && errorObject.error.errors && errorObject.error.errors[0]) {
          if (errorObject.error.errors[0].validationCode && errorObject.error.errors[0].validationCode !== 200) {
            return Observable.throw(this.resourceService.get(errorObject.error.errors[0].validationMessage));
          } else if (errorObject.status === 400) {
            return Observable.throw(errorObject.error.errors[0].defaultMessage + ' - ' +
              errorObject.error.errors['0'].rejectedValue);
          }
        }
        // JIRA LFWM - 1716 When the user provides any invalid filter value then the previous result data should be cleared
        this.pagedData.pageDefinition.recordCount = 0;
        this.pagedData.pageRows.splice(0, 0);
        this.searchStatus.hasResults = true;
        this.searchStatus.recordCount = 0;
        const event = new SearchEvent(NEW_SEARCH_EVENT);
        event.isSearchSuccess = false;
        this.publishEvent(event);
        // return Observable.throw('Failed to fetch  Search Results :: ' + errorObject.message);
      })
      .finally(() => { });
  }

  /**
   * Transforms the search criteria to a server specific format
   *
   * @param searchCriteria - The criteria to be searched
   * @param additionalParamMap - Map of additional parameters to be set in the request
   */
  public formatSearchCriteria(searchCriteria, additionalParamMap, filterCondition?: any, tabledId?: string) {
    if (!searchCriteria) {
      return {};
    }
    let allConditions = null;
    // Code added to merge filter conditions with master search criteria, if filter condition is there.
    if (filterCondition && filterCondition.clauses.length > 0) {
      allConditions = JSON.parse(JSON.stringify(searchCriteria.conditions));
      if (allConditions && allConditions.length > 0) {
        allConditions[allConditions.length - 1].operator = 'AND';
      }
      allConditions.push(filterCondition);
    } else {
      allConditions = searchCriteria.conditions;
    }
    const conditionReqList = [];
    allConditions.forEach((condition: any, index: any) => {
      const clauseReqlist = [];
      condition.clauses.forEach((clause: any) => {
        if (clause.value !== undefined && clause.value !== null) {
          if (tabledId && tabledId.toLowerCase() === "consigneeskutd") {
            clauseReqlist.push({
              column: "consigneeKey",
              operation: "=",
              value: clause.value,
              value1: clause.value1,
              logicalOperation: null
            });
          } else {
            clauseReqlist.push({
              column: clause.column,
              operation: clause.operation,
              value: clause.value,
              value1: clause.value1,
              logicalOperation: clause.logicalOperation
            });
          }
        }
      });
      if (clauseReqlist.length !== 0) {
        let logOp = null;
        if (index < allConditions.length - 1) {
          logOp = condition.operator;
        }
        conditionReqList.push({
          mandatory: condition.isMandatory,
          clauses: clauseReqlist,
          logicalOperation: logOp
        });
      }
    });
    if (additionalParamMap) {
      const additionalClauses = [];
      const additionalParams = additionalParamMap;
      const additionalProps = Object.keys(additionalParams);
      if (additionalProps.length > 0) {
        const lastCondition = conditionReqList.slice(conditionReqList.length - 1)[0];
        lastCondition.logicalOperation = 'AND';
        additionalProps.forEach((property: string, index: number) => {
          additionalClauses.push({
            column: property,
            operation: '=',
            value: additionalParams[property],
            logicalOperation: index === 0 ? '' : 'AND'
          });
        });
        conditionReqList.push({
          mandatory: false,
          clauses: additionalClauses,
          logicalOperation: ''
        });
      }
    }
    return { conditions: conditionReqList };
  }


  /**
   * Transforms the search response to the client format
   *
   * @param response - Server response
   */
  public transformSearchResponse(response: any) {
    const pDef: PageDefinition = this.pagedData.pageDefinition;
    const pData: Array<TableRow> = this.pagedData.pageRows;
    pDef.pageNumber = +response['pageNo'];
    pDef.sortColumn = response['sortColumn'];
    pDef.sortOrder = +response['sortOrder'];
    pDef.recordCount = +response['totalRecords'];
    pDef.pageSize = +response['pageSize'];
    /* [LFWM-1956] - for Inventory Screen - fetching aggregateRecords from loadbyPage response to show aggregate of some columns
    for records which is present in server side [this aggregateRecords used only in Inventory Screen] */
    pDef.aggregateRecords = response['aggregateRecords']; //
    for (let i = 0; i < response['pageData'].rows.length; i++) {
      // Adding code for DataTable View Structure Transformation
      const rowDefinition = response['pageData'].rows[i];
      const rowObject: any = new Object();
      rowObject.rIdx = rowDefinition.rIdx;
      // Flag to select/deselect the respective Checkbox (default : unchecked)
      rowObject.isSelected = false;
      // Iterating and inserting the server response cells to each Row Object in RowData Array
      rowObject.cells = {};
      rowDefinition.cells.forEach((cell: any) => {
        const cellInfo: any = new Object();
        cellInfo.cVal = cell.cVal;
        cellInfo.cValOrig = cell.cVal;
        cellInfo.cValPrev = cell.cVal;
        cellInfo.cName = cell.cName;
        rowObject.cells[cell.cName] = cellInfo;
      });
      pData.push(rowObject);
    }
  }

  /**
   * Method to perform search for a specific id (for eg, searching a document by its id)
   * @param idPropName - Id property
   * @param idPropVal - Value to be searched
   */
  public getItemDetailForId(idPropName: string, idPropVal: string): Observable<Response> {
    const storerKey = this.searchParams.storerKey;
    if (idPropName === 'sku' && storerKey) {
    // LFWM-2045 Creating search criteria for sku loadbypage by adding storerkey
      const searchCriteria = {
        conditions: [{
          mandatory: true,
          clauses: [{
            column: idPropName,
            operation: '=',
            value: idPropVal,
            logicalOperation: ''
          }],
          logicalOperation: 'AND'
        },
        {
          mandatory: true,
          clauses: [{
            column: 'storerKey',
            operation: '=',
            value: storerKey,
            logicalOperation: ''
          }],
          logicalOperation: 'AND'
        }]
      };
      return this.getFirstItemForSearchCriteria(searchCriteria);
    } else {
      const searchCriteria = {
        conditions: [{
          mandatory: true,
          clauses: [{
            column: idPropName,
            operation: '=',
            value: idPropVal,
            logicalOperation: ''
          }],
          logicalOperation: 'AND'
        }]
    };
    return this.getFirstItemForSearchCriteria(searchCriteria);
    }
  }

  /*
  * This method returns the first item corresponding to the given search criteria.
  * The formatCriteria flag indicates if the criteria needs to be formatted specific to the service request.
  */
  public getFirstItemForSearchCriteria(criteria: any): Observable<Response> {
    const pageInfoReq = {
      tableId: this.searchParams.tableId,
      pageNo: 1,
      sortColumn: this.pagedData.pageDefinition.sortColumn,
      sortOrder: this.pagedData.pageDefinition.sortOrder,
      pageSize: this.pagedData.pageDefinition.pageSize,
      fullFetch: true
    };
    const payload = {
      pageInfo: pageInfoReq,
      searchCriteria: criteria
    };
    const dataUrl$ = this.http$.post(`api/data/` + this.searchParams.tableId + `/loadByPage`, JSON.stringify(payload))
      .map((response: any) => {
        if (response['pageData'] === null || response['pageData'].rows === null
          || response['pageData'].rows.length <= 0) {
          return [];
        }
        return response['pageData'].rows[0];
      }).catch((error: any) => {
        return Observable.throw('Failed to fetch  Item details :: ' + error.message);
      });
    return dataUrl$;
  }

  /**
   * This method returns the next id for the given data table
   * @param moduleId - The module
   */
  public createUniqueId(moduleId: string) {
    if (moduleId === 'InvHold') {
      const dataUrl$ = this.http$.get('api/inbound/document/INVHOLDSTD/number')
        .catch((error: any) => {
          if (error.error.length !== 0) {
            if (error.error.statusCode !== 200) {
              return Observable.throw(this.resourceService.get(error.error.statusMessage));
            }
          }
        });
      return dataUrl$;
    } else {
      const dataUrl$ = this.http$.get(`api/inbound/document/` + moduleId + `/number`)
        .catch((error: any) => {
          return Observable.throw('Failed to generate Unique ID for ' + moduleId + ' :: ' + error.message);
        });
      return dataUrl$;
    }
  }

  /**
   * Method which publishes all the search events
   * @param event - Search Event
   */
  public publishEvent($event: SearchEvent) {
    this.searchEventSubject.next($event);
  }


  public receiveEvent():  Observable<SearchEvent> {
    return this.searchEventSubject.asObservable();
  }


  public getDisplayMode() {
    return  this.displayMode;
  }

  public setDisplayMode(displayMode: string) {
    this.displayMode = displayMode;
  }

  /**
   * Adds a new row locally (client side only) to the current data table. The new row will be put to the first position
   */
  public addNewRowToModel() {
    const rowData = this.createNewRow();
    const newRowEvent: SearchEvent = new SearchEvent(NEW_ROW_EVENT);
    newRowEvent.selectedRow = rowData;
    this.publishEvent(newRowEvent);
  }

 /**
   *  Insert a new row - seperating it from general method, as there could be further modifications
   * @param insertPosition : Index at which new row will be added to pagerows
  */
  public insertNewRow(insertPosition: number) {
    const rowData = this.createNewRow(insertPosition);
    const insertRowEvent: SearchEvent = new SearchEvent(INSERT_ROW_EVENT);
    insertRowEvent.selectedRow = rowData;
    // for INSERT_ROW_EVENT insertPosition will have the position at which new row should beinserted
    insertRowEvent.createdRowIndex = insertPosition;
    this.publishEvent(insertRowEvent);
  }

  /**
   *  Duplicate a row. duplicate acts as insert with difference only in event receiver actions
   * which might copy entire row or specific columns
   * @param insertPosition : Index at which duplicated row will be added to pagerows
  */
  public duplicateRow(insertPosition: number) {
    const rowData = this.createNewRow(insertPosition);
    const duplRowEvent: SearchEvent = new SearchEvent(DUPLICATE_ROW_EVENT);
    duplRowEvent.selectedRow = rowData;
    // for Duplicate_ROW_EVENT insertPosition will have the position at which new row should beinserted
    duplRowEvent.createdRowIndex = insertPosition;
    this.publishEvent(duplRowEvent);
  }

  /**
   * This method add a new row to the specified position. If the position is undefined,
   * the row is added to the zeroth position
   * @param insertPosition - The insertPosition
   * @returns new rows rowData
   */
  protected createNewRow(insertPosition?: number): any {
    // if insertPostion is not given it will work as add new row , if insert postion is given new row will be inserted at that position
    if (!this.metadata || !this.metadata['columnList']) {
      return 'meta data not loaded';
    }
    const cellData = {};
    this.metadata['columnList'].map((column: any) => {
      const propName = column.propertyName;
      const propType = column.type;
      const dateDefaultValue = column.defaultVal;
      if (propType === 'TEXTFIELD') {
        let defaultValue: any;
        if (column.dataType === 'Integer') {
          if (column.defaultVal && column.defaultVal !== '') {
            defaultValue = column.defaultVal;
          } else {
            defaultValue = 0;
          }
        } else if (column.dataType === 'String') {
          if (column.defaultVal && column.defaultVal !== '') {
            defaultValue = column.defaultVal;
          } else {
            defaultValue = '';
          }
        } else if (column.dataType === 'Float') {
          if (column.defaultVal && column.defaultVal !== '') {
            defaultValue = column.defaultVal;
          } else {
            defaultValue = 0.000000;
          }
        } else if (column.dataType === 'Currency') {
          if (column.defaultVal && column.defaultVal !== '') {
            defaultValue = column.defaultVal;
          } else {
            defaultValue = '0.00';
          }
        } else {
          defaultValue = null;
        }
        cellData[propName] = { cVal: defaultValue, cName: propName };
      } else if (propType === 'DATE' || propType === 'DATETIME') {
        // To set null value if no default value starts
       if (!cellData[dateDefaultValue]) {
          cellData[propName] = {cName: propName, cVal: null };
        } else {
          if (propType === 'DATE') {
            cellData[propName] = {cName: propName, cVal: this.dateConfigService.getFormattedDisplayDate(Date.now()) };
          } else if (propType === 'DATETIME') {
            cellData[propName] = { cName: propName, cVal: this.dateConfigService.getFormattedDisplayDateTime(Date.now()) };
          }
        }
        // To set null value if no default value ends

      } else if (propType === 'DROPDOWN' || propType === 'EDITABLEDROPDOWN') {
        let defaultOption = '';
        if (column.values[0] && column.values[0].key) {
          defaultOption = column.defaultVal; // To set the default value if the user does not select any options
        }
        cellData[propName] = { cName: propName, cVal: defaultOption };
      } else if (propType === 'CHECKBOX' || propType === 'CHECKBOX_CHR') {
        // Adding CHECKBOX_CHR type denoting the saved value will be 'Y' or 'N'
        // CHECKBOX type denoting the saved value will be '1' or '0'
        cellData[propName] = { cVal: false };
      } else if (propType === 'DROPDOWN_MC') {
        let defaultValue: any;
        defaultValue = '';
        cellData[propName] = {cName: propName, cVal: defaultValue };
      } else if (propType === 'DROPDOWN_MS') {
        let defaultValue: any;
        if (column.defaultVal && column.defaultVal !== '') {
          defaultValue = column.defaultVal;
        } else {
          defaultValue = '';
        }
        cellData[propName] = {cName: propName, cVal: defaultValue };
      }else if (propType === 'TIME24') {
        let defaultValue: any;
        defaultValue = '00:00';
        cellData[propName] = {cName: propName, cVal: defaultValue };
      }
    });
    cellData['isNewItem'] = { cVal: true };
    const rowData = new TableRow();
    rowData.isSelected = false;
    rowData.isEdited = true;
    rowData.isNewRow = true;
    rowData.cells = cellData;
    this.pagedData.pageDefinition.recordCount++;
    rowData['rIdx'] = this.pagedData.pageRows.length + 1;
    if (!insertPosition) { // addmode
      // changing the logic as part of JIRA-649 to display the newly added row in the top above all rows in the grid
      if (!this.pagedData.pageRows) {
        this.pagedData.pageRows = [];
        this.pagedData.pageRows.push(rowData);
      } else {
        this.pagedData.pageRows.splice(0, 0, rowData);
      }
    } else {
      // mode === insert
      this.pagedData.pageRows.splice(insertPosition, 0, rowData);

    }
    // this.pagedData.pageRows.push(rowData);
    if (!this.searchStatus.hasResults) {
      this.searchStatus.hasResults = true;
    }
    if(this.searchParams.tableId == "asndetailtd" || this.searchParams.tableId == "trdetailtd"){
      if(this.pagedData.pageRows && this.pagedData.pageRows.length > 1){
        let max = this.pagedData.pageRows[0].cells['externLineNo'].cVal;
        for(let i=0;i< this.pagedData.pageRows.length;i++){
          if(this.pagedData.pageRows[i].cells['externLineNo'].cVal){
            if(max < this.pagedData.pageRows[i].cells['externLineNo'].cVal)
              max = this.pagedData.pageRows[i].cells['externLineNo'].cVal
          }
        }

        if(this.pagedData.pageRows[this.pagedData.pageRows.length-1].cells && this.pagedData.pageRows[this.pagedData.pageRows.length-1].cells['externReceiptKey'].cVal){
          rowData.cells['externReceiptKey'].cVal = this.pagedData.pageRows[this.pagedData.pageRows.length-1].cells['externReceiptKey'].cVal;
        }
        else{
          rowData.cells['externReceiptKey'].cVal = this.searchParams.tableEditor.primaryData['externReceiptkey'].cVal;
        }

        if(max){
          let Leadzeros = max.match('0*')[0];
          rowData.cells['externLineNo'].cVal = Leadzeros+ (Number(max)+1).toString();
        }
        else{
          rowData.cells['externLineNo'].cVal = '';
        }

      }else if(this.pagedData.pageRows && this.pagedData.pageRows.length == 1){
        rowData.cells['externLineNo'].cVal = '';
        rowData.cells['externReceiptKey'].cVal =  this.searchParams.tableEditor.primaryData['externReceiptkey'].cVal;
      }
      if(!rowData.cells['status'].cVal)
        rowData.cells['status'].cVal = "0";
    }
    if(this.searchParams.tableId == "pmmanagedetailtd" ){
      if(!rowData.cells['status'].cVal)
        rowData.cells['status'].cVal = "0";
    }
    return rowData;
  }


  /**
   * Deletes the selected row locally (client side only)
   *
   * @param idProperty - Id property
   * @param rowsToDelete - Value
   */
  public deleteRowsFromModel(idProperty: any, rowsToDelete: any) {
    if (this.metadata === null || this.metadata['columnList'] === null) {
      return 'meta data not loaded';
    }
    if (this.pagedData.pageRows === undefined || this.pagedData.pageRows === null) {
      return 'page data not loaded';
    }
    this.pagedData.pageRows = this.pagedData.pageRows.filter((row: any) => {
      if (row.cells === null || !row.cells[idProperty] || row.cells[idProperty] === null) {
        return true;
      } else if (rowsToDelete.indexOf(row.cells[idProperty].cVal) !== -1) {
        return false;
      } else {
        return true;
      }
    });
    this.pagedData.pageDefinition.recordCount = this.pagedData.pageDefinition.recordCount - rowsToDelete.length;
    const evt = new SearchEvent(DELETE_ROW_EVENT);
    this.publishEvent(evt);
  }

  /*This function delete the newly added row, upon clicking on the 'x' appearing on the row */
  public deleteNewRowFromModel(rowIndex) {
    if (this.metadata === null || this.metadata['columnList'] === null) {
      return 'meta data not loaded';
    }
    if (this.pagedData.pageRows === undefined || this.pagedData.pageRows === null) {
      return 'page data not loaded';
    }
    this.pagedData.pageRows.splice(rowIndex, 1);
    this.pagedData.pageDefinition.recordCount = this.pagedData.pageDefinition.recordCount - 1;
    const evt = new SearchEvent(DELETE_ROW_EVENT);
    this.publishEvent(evt);
  }

  public saveSearchPreference(request: any) {
    const response$ = this.http$.post(`api/data/saveSearch`, request)
      .map((response: any) => {
        return response;
      }).catch((error: any) => {
        if (error.error.errors.length !== 0) {
          if (error.error.errors[0].validationCode !== 200) {
            return Observable.throw(error.error.errors['0'].validationMessage);
          }
        }
      });
    return response$;
  }

  private transformTablePreference(dataTableId, response) {
    const tablePreference = new TablePreference();
    if (response && response.length > 0) {
      response.forEach(preference => {
        const preferenceModel = new Preference();
        preferenceModel.preferenceId = preference['preferenceId'];
        preferenceModel.prefType = preference['prefType'];
        preferenceModel.dataTableId = dataTableId;
        preferenceModel.configuration = JSON.parse(preference['configuration']);
        if (preferenceModel.prefType === 'FAVOURITECONFIG') {
          tablePreference.favouriteConfig = preferenceModel;
        } else if (preferenceModel.prefType === 'COLUMNCONFIG') {
          tablePreference.columnConfig = preferenceModel;
        } else if (preferenceModel.prefType === 'COLUMNGROUPCONFIG') {
          tablePreference.columGroupConfig = preferenceModel;
        }
      });
    }

    if (!tablePreference.favouriteConfig) {
      const preferenceModelFav = new Preference();
      preferenceModelFav.preferenceId = null;
      preferenceModelFav.prefType = 'FAVOURITECONFIG';
      preferenceModelFav.dataTableId = dataTableId;
      preferenceModelFav.configuration = { 'favourite': false };
      tablePreference.favouriteConfig = preferenceModelFav;
    }

    if (!tablePreference.columnConfig) {
      const preferenceModelColumnConfig = new Preference();
      preferenceModelColumnConfig.preferenceId = null;
      preferenceModelColumnConfig.prefType = 'COLUMNCONFIG';
      preferenceModelColumnConfig.dataTableId = dataTableId;
      preferenceModelColumnConfig.configuration = {
        hiddenPropertiesList: [],
        pinnedPropertiesList: [],
        columnWidthsObject: {},
        propertyIndexMap: {},
        pageDefinitionObject: {
          'sortColumn': null,
          'sortOrder': 0,
          'pageSize': 10
        }
      };
      tablePreference.columnConfig = preferenceModelColumnConfig;
    }

    if (!tablePreference.columGroupConfig) {
      const preferenceModelColGrp = new Preference();
      preferenceModelColGrp.preferenceId = null;
      preferenceModelColGrp.prefType = 'COLUMNGROUPCONFIG';
      preferenceModelColGrp.dataTableId = dataTableId;
      preferenceModelColGrp.configuration = {};
      tablePreference.columGroupConfig = preferenceModelColGrp;
    }
    return tablePreference;
  }

  /**
   * Method to get the maximum value for a column in the table
   */
  public getMaxValueForIdProperty() {
    const response$ = this.http$.post(`api/data/` + this.searchParams.tableId + `/function`,
      {
        'functionType': 'MAX',
        'propertyNames': [this.searchParams.idProperty],
        'filterCriteria': this.searchCriteria
      })
      .catch((error: any) => {
        return Observable.throw('Could not fetch the max value');
      });
    return response$;
  }

  public setNewPageEventInProgress(newPageEventInProgress: any) {
    this.newPageEventInProgress = newPageEventInProgress;
  }

  public getNewPageEventInProgress(): boolean {
    return this.newPageEventInProgress;
  }

  public clone() {
    const targetService: SearchService = new SearchService(this.http$, this.searchParams,
      this.searchStatus, this.resourceService, null, this.alertService, this.dateConfigService);
    targetService.metadata = this.metadata;
    targetService.pagedData = this.pagedData;
    targetService.searchCriteria = this.searchCriteria;
    targetService.newPageEventInProgress = this.newPageEventInProgress;
    targetService.displayMode = this.displayMode;
    return targetService;
  }

  public fetchAutoSuggestionResults(isNextPage: boolean): Observable<Response> {
    this.pagedData.pageRows = [];
    const tableId = this.searchParams.tableId;
    if (isNextPage) {
      this.pagedData.pageDefinition.pageNumber++;
    } else {
      this.pagedData.pageDefinition.pageNumber = 1;
    }
    const pageInfoReq = {
      tableId: tableId,
      pageNo: this.pagedData.pageDefinition.pageNumber,
      pageSize: this.pagedData.pageDefinition.pageSize,
      fullFetch: this.searchParams.isFullFetch
    };
    const payload = {
      pageInfo: pageInfoReq,
      searchCriteria: this.formatSearchCriteria(this.searchCriteria, this.searchParams.additionalParamMap)
    };
    const dataUrl$ = this.http$.post(`api/data/` + this.searchParams.tableId + `/autosuggest`, JSON.stringify(payload))
      .map((response: any) => {
        // these code is added to avoid duplicate entries when consecutives keys are pressed fastly
        if (!isNextPage) {
          this.pagedData.pageRows = [];
        }
        // LFWM-4542 From Location validation when totalRecords are zero 
        if(response.tableId === 'locationtd' && response.totalRecords == 0) {
          return this.alertService.clearAll().error(this.resourceService.get('msg_err_task_from_loc'));
        } else {
          this.alertService.clearAll();
        }
        return this.transformSearchResponse(response);
      })
      .catch((error: any) => {
        if (error.error && error.error.errors && error.error.errors[0]) {
          if (error.status === 400) {
            return Observable.throw(error.error.errors[0].defaultMessage + ' - ' +
              error.error.errors['0'].rejectedValue);
          }
        } else {
          return Observable.throw('Failed to fetch  Search Results :: ' + error.message);
        }
      });
    return dataUrl$;
  }

  /*  public fetchAutoSuggestionResultsForFilter(): Observable<Response> {
      this.pagedData.pageRows = [];
      const tableId = this.searchParams.tableId;
      this.pagedData.pageDefinition.pageNumber = 1;
      const pageInfoReq = {
        tableId: tableId,
        pageNo: this.pagedData.pageDefinition.pageNumber,
        pageSize: this.pagedData.pageDefinition.pageSize,
        fullFetch: this.searchParams.isFullFetch
      };
      const payload = {
        pageInfo: pageInfoReq,
        searchCriteria: this.formatSearchCriteria(this.searchCriteria, this.searchParams.additionalParamMap, this.filterCondition)
      };
      const dataUrl$ = this.http$.post(`api/data/` + this.searchParams.tableId + `/autosuggest`, JSON.stringify(payload))
        .map((response: any) => {
          return this.transformSearchResponse(response);
        })
        .catch((error: any) => {
          return Observable.throw('Failed to fetch  Search Results :: ' + error.message);
        });
      return dataUrl$;
    } */

  /*  public performFilter() {
      this.fetchAutoSuggestionResultsForFilter()
        .subscribe((data: any) => {
          this.searchStatus.hasResults = true;
          this.searchStatus.recordCount = this.pagedData.pageDefinition.recordCount;
          const event = new SearchEvent(NEW_SEARCH_EVENT);
          event.isSearchSuccess = true;
          this.publishEvent(event);
        });
    } */

  public exportData(format: string, tableId?: string) {
    const tableIdFromSearchParams = this.searchParams.tableId;
    if (tableId == null) {
      tableId = tableIdFromSearchParams;
    }
    const pageInfoReq = {
      tableId: tableId,
      sortColumn: this.pagedData.pageDefinition.sortColumn,
      sortOrder: this.pagedData.pageDefinition.sortOrder,
      fullFetch: this.searchParams.isFullFetch
    };
    const payload = {
      pageInfo: pageInfoReq,
      searchCriteria: this.formatSearchCriteria(this.searchCriteria, this.searchParams.additionalParamMap, this.filterCondition)
    };
    // 2801 -  added change to display error if export fails
    const response$ = this.http$.post('api/data/' + tableId + '/export/' + format, payload)
      .map((response: any) => {
        return response;
      })
      .catch((error: any) => {
        if (error.error.length !== 0) {
           if (error.error.errors[0].validationCode !== 200) {
             return Observable.throw(error.error.errors['0'].validationMessage);
           }
         }
       });
    return response$;
  }

  public exportDataWithoutPrivilege(format: string, tableId?: string) {
    const tableIdFromSearchParams = this.searchParams.tableId;
    if (tableId == null) {
      tableId = tableIdFromSearchParams;
    }
    const pageInfoReq = {
      tableId: tableId,
      sortColumn: this.pagedData.pageDefinition.sortColumn,
      sortOrder: this.pagedData.pageDefinition.sortOrder,
      fullFetch: this.searchParams.isFullFetch
    };
    const payload = {
      pageInfo: pageInfoReq,
      searchCriteria: this.formatSearchCriteria(this.searchCriteria, this.searchParams.additionalParamMap, this.filterCondition)
    };
    // 2801 -  added change to display error if export fails
    const response$ = this.http$.post('api/data/' + tableId + '/export/' + format + '/stockTakeParameter', payload)
      .map((response: any) => {
        return response;
      })
      .catch((error: any) => {
        if (error.error.length !== 0) {
          if (error.error.errors[0].validationCode !== 200) {
            return Observable.throw(error.error.errors['0'].validationMessage);
          }
        }
      });
    return response$;
  }



  // method returns the metadata for the given property
  public getMetadataForProperty(property) {
    let columnMetaData: any = null;
    if (this.metadata && property) {
      this.metadata.columnList.forEach(column => {
        if (column.propertyName === property) {
          columnMetaData = column;
          return;
        }
      });
    }
    return columnMetaData;
  }
  @LoaderEnabled()
  public copyFetchSearchResults(pageSize: any, pageNumber: any): Observable<Response> {
    this.pagedData.pageRows = [];
    let tableId;
    if (this.searchParams.fetchArchiveRecords && this.searchParams.archiveTableId) {
      tableId = this.searchParams.archiveTableId;
    } else {
      tableId = this.searchParams.tableId;
    }
    const pageInfoReq = {
      tableId: tableId,
      pageNo: pageNumber,
      sortColumn: this.pagedData.pageDefinition.sortColumn,
      sortOrder: this.pagedData.pageDefinition.sortOrder,
      pageSize: pageSize,
      fullFetch: this.searchParams.isFullFetch,
      fetchAllrecords: this.searchParams.paginateLocally,
      fetchArchiveRecords: this.searchParams.fetchArchiveRecords,
    };
    const payload = {
      pageInfo: pageInfoReq,
      searchCriteria:
        this.formatSearchCriteria(this.searchCriteria, this.searchParams.additionalParamMap, this.filterCondition, pageInfoReq.tableId)
    };
    const dataUrl$: any
      = this.http$.post(`api/data/` + tableId
        + `/loadByPage`, JSON.stringify(payload));
    return dataUrl$
      .map((response: any) => {
        return this.transformSearchResponseForCopyValue(response.pageData);
      })
      .catch((errorObject: any) => {
        if (errorObject.error && errorObject.error.errors && errorObject.error.errors[0]) {
          if (errorObject.error.errors[0].validationCode && errorObject.error.errors[0].validationCode !== 200) {
            return Observable.throw(this.resourceService.get(errorObject.error.errors[0].validationMessage));
          } else if (errorObject.status === 400) {
            return Observable.throw(errorObject.error.errors[0].defaultMessage + ' - ' +
              errorObject.error.errors['0'].rejectedValue);
          }
        }
      });
  }
  public transformSearchResponseForCopyValue(pageData: any){
    const pData: Array<TableRow> = [];
    for (let i = 0; i < pageData.rows.length; i++) {
      // Adding code for DataTable View Structure Transformation
      const rowDefinition = pageData.rows[i];
      const rowObject: any = new Object();
      rowObject.rIdx = rowDefinition.rIdx;
      // Flag to select/deselect the respective Checkbox (default : unchecked)
      rowObject.isSelected = false;
      rowObject.isEdited = false;
      // Iterating and inserting the server response cells to each Row Object in RowData Array
      rowObject.cells = {};
      rowDefinition.cells.forEach((cell: any) => {
        const cellInfo: any = new Object();
        cellInfo.cVal = cell.cVal;
        cellInfo.cValOrig = cell.cVal;
        cellInfo.cValPrev = cell.cVal;
        cellInfo.cName = cell.cName;
        rowObject.cells[cell.cName] = cellInfo;
      });
      pData.push(rowObject);
    }
    const rowInfo: any = new Object();
    rowInfo.rows = pData;
    return (rowInfo);
  }
  public fetchSku(request: any): Observable<Response> {
    const url = `api/data/` + 'commoditytd' + `/loadByPage`;
    const response$ = this.http$.post(url, JSON.stringify(request))
      .map((response: any) => {
        return response;
      })
      .catch((error: any) => {
        if (error.length !== 0) {
          if (error.error.statusCode !== 200) {
            return Observable.throw(error.error.statusMessage);
          }
        } else if (error.error.length !== 0) {
          if (error.error.statusCode !== 200) {
            return Observable.throw(error.error.statusMessage);
          }
        }
      });
    return response$;
  }

  public getSelectedFieldValueFromSearchCriteria(selectedField) {
    let reutrnValue: any = '';
    if (this.searchCriteria && this.searchCriteria.conditions) {
      for (let condition of this.searchCriteria.conditions) {
        for (let clause of condition.clauses) {
          if (clause.column === selectedField) {
            reutrnValue = clause.value;
            break;
          }
          if (reutrnValue && reutrnValue !== '') {
            break;
          }
        }
      }
    }
    return reutrnValue;
  }


  /**
   * This method will fetch the Table Column Attribute for the search entity. 
   */
  public fetchTableColumnAttribute(): Observable<Response> {
    let tableId = this.searchParams.tableId;
    const url = 'api/core/columnattribute/' + tableId;
    const response$ = this.http$.get(url)
      .map((response: any) => {
        this.tableColumnAttribute = response;
        return response;
      })
      .catch((error: any) => {
        if (error.length !== 0) {
          if (error.error.statusCode !== 200) {
            return Observable.throw(error.error.statusMessage);
          }
        } else if (error.error.length !== 0) {
          if (error.error.statusCode !== 200) {
            return Observable.throw(error.error.statusMessage);
          }
        }
      });
    return response$;

  }

  public getTableColumnAttribute(): any {
    return this.tableColumnAttribute;
  }

  public setTableColumnAttribute(tableColumnAttribute: any) {
    this.tableColumnAttribute = tableColumnAttribute;
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/1_SCE_CI_CD_SIT/src/app/shared/search/search.service.ts